import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { getReportConfigByUniqueIdentifierWeb } from 'app-components/flux/report-configurations/reportConfigurationsSelectors';
import type { ReportNameType } from 'app-components/flux/report-configurations/reportConfigurationsTypes';

import { transformReportConfigurationToState } from 'containers/NewReports/transformers';
import type { ReportsState } from 'containers/NewReports/types';
import { getDefaultForTabName } from 'containers/NewReports/utils';

export const useReportState = ({ tabName }: { tabName: ReportNameType }): ReportsState => {
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);
  const openBusinessesIds = useAppSelector((state) => businessSelectors.getOpenBusinesses(state)).reduce<string[]>(
    (acc, business) => {
      business.id && acc.push(business.id);
      return acc;
    },
    [],
  );
  const reportConfiguration = useAppSelector((state) =>
    getReportConfigByUniqueIdentifierWeb(state, { persistentType: 'TEMPORARY', getTabName: () => tabName }),
  );

  const defaultState = useMemo(() => {
    return {
      ...getDefaultForTabName(tabName, !!businessEnabled, defaultBusiness?.id || '', openBusinessesIds),
      name: tabName,
      today: DateTime.local(),
      breadcrumbs: [],
    };
  }, [businessEnabled, defaultBusiness, openBusinessesIds, tabName]);

  const reportState = reportConfiguration ? transformReportConfigurationToState(reportConfiguration) : defaultState;

  return reportState;
};
